'use client'
import { useState, useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { motion, AnimatePresence } from "motion/react";
import Navlink from "../../General Components/Navlink";
import LogoButton from "./LogoButton";
import IconLink from "../../General Components/IconLink";
import { useFilter } from "@/app/context/FilterContext";

const Navbar = () => {
  const pathname = usePathname();
  const [menuOpen, setMenuOpen] = useState(false);
  const { activeFilter, setActiveFilter, filters } = useFilter();

  // Show hint popup below logo only once ever (first filter applied)
  const [showHint, setShowHint] = useState(false);
  const hintTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hintShownRef = useRef(false);

  useEffect(() => {
    if (activeFilter !== null && !hintShownRef.current) {
      // Check localStorage — only show if never seen before
      if (!localStorage.getItem('hasSeenFilterHint')) {
        hintShownRef.current = true;
        localStorage.setItem('hasSeenFilterHint', '1');
        setShowHint(true);
        hintTimer.current = setTimeout(() => setShowHint(false), 5000);
      }
    }
    return () => { if (hintTimer.current) clearTimeout(hintTimer.current); };
  }, [activeFilter]);

  return (
    <div className="navbar flex flex-col">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div style={{ position: "relative" }}>
            <LogoButton />
            <AnimatePresence>
              {showHint && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                  style={{
                    position: "absolute",
                    top: "calc(100% + 6px)",
                    left: 0,
                    background: "var(--background2)",
                    border: "1px solid var(--uforeground2)",
                    borderRadius: 8,
                    padding: "6px 10px",
                    fontSize: "0.7rem",
                    color: "var(--foreground2)",
                    whiteSpace: "nowrap",
                    lineHeight: 1.4,
                    zIndex: 50,
                    pointerEvents: "none",
                  }}
                >
                  Click the role here to reset filter at any time
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Desktop nav */}
        <div className="hidden sm:flex items-center text-nowrap">
          <Navlink link="/" isActive={pathname === "/"}>Home</Navlink>
          <Navlink link="/projects" isActive={pathname === "/projects"}>Projects</Navlink>
          <Navlink link="/games" isActive={pathname === "/games"}>Games</Navlink>
          <Navlink link="/contact" isActive={pathname === "/contact"}>Contact</Navlink>
          <div className="vertical-line"></div>
          <IconLink
            link="https://github.com/Codster177"
            image="/Icons/Websites/Github-Icon.svg"
            label="GitHub"
          />
          <IconLink
            link="https://codster177.itch.io"
            image="/Icons/Websites/Itch-Icon.svg"
            label="Itch.io"
          />
          <IconLink
            link="https://www.linkedin.com/in/cody-taylor-36a6aa24b/"
            image="/Icons/Websites/LinkedIn_icon.svg"
            label="LinkedIn"
          />
        </div>

        {/* Mobile hamburger button */}
        <button
          className="sm:hidden flex flex-col justify-center gap-1.5 p-2 mr-1 border-none group"
          style={{ background: "transparent" }}
          onClick={() => setMenuOpen((o) => !o)}
          aria-label="Toggle navigation menu"
          aria-expanded={menuOpen}
        >
          <span className="block w-6 h-0.5 bg-red-500 group-hover:bg-white rounded-full transition-colors duration-200" />
          <span className="block w-6 h-0.5 bg-red-500 group-hover:bg-white rounded-full transition-colors duration-200" />
          <span className="block w-6 h-0.5 bg-red-500 group-hover:bg-white rounded-full transition-colors duration-200" />
        </button>
      </div>

      {/* Mobile dropdown */}
      {menuOpen && (
        <div className="sm:hidden flex flex-col items-start pb-3 pt-1 border-t border-(--uforeground2) mt-1">
          <Navlink link="/" isActive={pathname === "/"}>Home</Navlink>
          <Navlink link="/projects" isActive={pathname === "/projects"}>Projects</Navlink>
          <Navlink link="/games" isActive={pathname === "/games"}>Games</Navlink>
          <Navlink link="/contact" isActive={pathname === "/contact"}>Contact</Navlink>
          <div className="flex mt-2 ml-1">
            <IconLink
              link="https://github.com/Codster177"
              image="/Icons/Websites/Github-Icon.svg"
              label="GitHub"
            />
            <IconLink
              link="https://codster177.itch.io"
              image="/Icons/Websites/Itch-Icon.svg"
              label="Itch.io"
            />
            <IconLink
              link="https://www.linkedin.com/in/cody-taylor-36a6aa24b/"
              image="/Icons/Websites/LinkedIn_icon.svg"
              label="LinkedIn"
            />
          </div>

          {/* Mobile filter selector */}
          {filters.length > 0 && (
            <div className="mt-3 w-full border-t border-(--uforeground2) pt-3">
              <p className="text-xs text-(--foreground2) px-1 mb-2 uppercase tracking-wider">Filter by</p>
              <div className="flex flex-wrap gap-2 px-1">
                {activeFilter && (
                  <button
                    onClick={() => setActiveFilter(null)}
                    style={{
                      background: 'transparent',
                      border: '1px solid var(--uforeground2)',
                      borderRadius: 8,
                      padding: '4px 10px',
                      cursor: 'pointer',
                      color: 'var(--foreground2)',
                      fontSize: '0.85em',
                      fontFamily: 'inherit',
                    }}
                  >
                    Clear
                  </button>
                )}
                {filters.map((name) => (
                  <button
                    key={name}
                    onClick={() => setActiveFilter(name)}
                    style={{
                      background: activeFilter === name ? 'var(--uforeground2)' : 'transparent',
                      border: '1px solid var(--uforeground2)',
                      borderRadius: 8,
                      padding: '4px 10px',
                      cursor: 'pointer',
                      color: activeFilter === name ? '#fff' : 'var(--foreground)',
                      fontSize: '0.85em',
                      fontFamily: 'inherit',
                    }}
                  >
                    {name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Navbar;
